import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useCloseOpenShiftSuggestion } from "../../contexts/CloseOpenShiftSuggestionProvider";
import { useDraftMode } from "../../contexts/DraftModeContext";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { isInPTO } from "../../utils/dateUtils";
import { isNurseActiveInDepartment } from "../../utils/formatters";
import { LocationMode, type Nurse, NurseFullRoleShort, type NurseInShift, NurseInShiftCreationSource, NurseRole } from "../../utils/models";
import { findRedundantOpenShift } from "../../utils/openShiftMatching";
import { countDailyHours } from "../../utils/statsCalculations";
import { getShiftIndexByShiftTimes, getShiftWorkHours } from "../../utils/utilFunctions";
import { useStaffingTargetCheck } from "../../utils/warningDialogs/useStaffingTargetCheck";
import { buildEmployeeStatusWarning, isAgencyNurse, useNurseWarnings, WarningDialog } from "../../utils/warningDialogs/WarningDialog";
import { EditTimePopover } from "../EditTimePopover";
import { StaffAutoComplete } from "../UIToolkit/Inputs/StaffAutoComplete";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { JobSelector } from "../UIToolkit/molecules/JobSelector";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";

type QuickAddNurseDialogProps = {
	date: DateTime;
	weekIndex: number;
	dayIndex: number;
	showActual: boolean;
	sectionName: string;
	sectionLocationKey: string;
	selectedTimeRange?: { start: DateTime; end: DateTime };
	onClose: () => void;
};

export const QuickAddNurseDialog: FC<QuickAddNurseDialogProps> = ({
	date,
	weekIndex,
	dayIndex,
	showActual,
	sectionName,
	sectionLocationKey,
	selectedTimeRange,
	onClose,
}) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { hospitalData, chosenDepartment, prepareDataForAddNurse, timezone, isHospital } = useHospitalData();
	const formatPersonName = useFormatPersonName();
	const { settings } = useUserSettingsContext();
	const { staffRequests } = useRequestsContext();
	const { isDraftMode } = useDraftMode();
	const suggestCloseOpenShift = useCloseOpenShiftSuggestion();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const {
		addNurse,
		isLoading: { addNurse: addNurseLoading },
	} = useHandleNurseChange();
	const [selectedNurse, setSelectedNurse] = useState<Nurse | null>(null);
	// Defaults to the nurse's first job, which is what prepareDataForAddNurse would have picked
	// anyway — the dropdown just makes that choice visible and changeable for a multi-job nurse.
	const [selectedJobId, setSelectedJobId] = useState<string | undefined>(undefined);

	const nurseJobs = useMemo(() => selectedNurse?.jobs ?? [], [selectedNurse]);
	const selectedJob = useMemo(() => nurseJobs.find((job) => job.id === selectedJobId), [nurseJobs, selectedJobId]);

	const handleNurseChange = useCallback((nurse: Nurse | null) => {
		setSelectedNurse(nurse);
		setSelectedJobId(nurse?.jobs?.[0]?.id);
	}, []);

	const locationOptions = useMemo<SystemAutoCompleteOption<string>[]>(() => {
		const uniqueLocations = Array.from(new Set(chosenDepartment.locations.map((loc) => loc.location))).sort((a, b) => a.localeCompare(b));
		return uniqueLocations.map((loc) => ({ label: loc, value: loc }));
	}, [chosenDepartment.locations]);

	// Default to first location in the originating section
	const [selectedLocation, setSelectedLocation] = useState<string | null>(() => {
		const sectionLocation = chosenDepartment.locations.find((loc) => {
			if (settings.censusByLocation === LocationMode.Floor) return loc.floor === sectionLocationKey;
			if (settings.censusByLocation === LocationMode.Unit) return loc.unit === sectionLocationKey;
			return false;
		});
		return sectionLocation?.location ?? locationOptions[0]?.value ?? null;
	});

	const defaultStartTime = useMemo(() => {
		if (selectedTimeRange) return selectedTimeRange.start.setZone(timezone);
		// Fall back to first shift's start time
		const firstShift = hospitalData.weeks[weekIndex]?.weeklyShifts[0];
		if (firstShift?.days[dayIndex]?.date) {
			return firstShift.days[dayIndex].date.setZone(timezone).set({ hour: 7, minute: 0 });
		}
		return date.setZone(timezone).set({ hour: 7, minute: 0 });
	}, [selectedTimeRange, hospitalData.weeks, weekIndex, dayIndex, date, timezone]);

	const defaultEndTime = useMemo(() => {
		if (selectedTimeRange) return selectedTimeRange.end.setZone(timezone);
		return defaultStartTime.plus({ hours: 12 });
	}, [selectedTimeRange, defaultStartTime, timezone]);

	const [startTime, setStartTime] = useState<DateTime>(defaultStartTime);
	const [endTime, setEndTime] = useState<DateTime>(defaultEndTime);

	const nurseOptions = useMemo(() => {
		return hospitalData.nurses
			.filter((nurse) => isNurseActiveInDepartment(nurse, chosenDepartment.department_id))
			.map((nurse) => ({
				label: formatPersonName(nurse),
				value: nurse,
			}));
	}, [hospitalData.nurses, chosenDepartment.department_id, formatPersonName]);

	const performSave = useCallback(async () => {
		if (!selectedNurse) return;

		const shiftTimes = { start: startTime, end: endTime, date };
		const shiftNo = getShiftIndexByShiftTimes({ startTime, endTime });

		const nurseToAdd: NurseInShift | undefined = prepareDataForAddNurse(
			hospitalData,
			weekIndex,
			dayIndex,
			shiftNo,
			selectedNurse.id,
			selectedLocation ?? undefined,
			showActual,
			undefined,
			shiftTimes,
		);

		if (nurseToAdd) {
			// prepareDataForAddNurse always stamps the nurse's first job; honour the picker instead so
			// the shift lands on the job whose shift lengths the time picker just offered.
			if (selectedJob) {
				nurseToAdd.jobId = selectedJob.id;
				nurseToAdd.jobName = selectedJob.displayName;
			}

			// Resolved before the add, since adding refreshes the requests this reads from.
			const redundantOpenShift =
				showActual || isDraftMode || isHospital
					? undefined
					: findRedundantOpenShift(staffRequests, {
							departmentId: chosenDepartment.department_id,
							shiftStart: startTime,
							shiftEnd: endTime,
							location: selectedLocation ?? "",
							jobId: nurseToAdd.jobId,
							role: selectedNurse.role,
							jobManagementEnabled,
							isCharge: nurseToAdd.charge,
						});

			await addNurse({
				nurse: nurseToAdd,
				shiftIndex: shiftNo,
				dayNo: dayIndex,
				weekIndex,
				location: selectedLocation,
				isActual: showActual,
				source: NurseInShiftCreationSource.AddNurse,
				shiftTimes: { start: startTime, end: endTime },
				date,
			});
			track("quick_add_nurse_save", {
				nurse_id: selectedNurse.id,
				job_id: nurseToAdd.jobId,
				location: selectedLocation,
				section_name: sectionName,
				section_location_key: sectionLocationKey,
				start_time: startTime.toFormat("HH:mm"),
				end_time: endTime.toFormat("HH:mm"),
			});

			// Before the prompt, which is hosted at the app root — otherwise this form stays painted under it.
			onClose();

			if (redundantOpenShift) {
				await suggestCloseOpenShift({ request: redundantOpenShift, source: "quick_add_nurse_dialog" });
			}
			return;
		}

		onClose();
	}, [
		selectedNurse,
		selectedLocation,
		startTime,
		endTime,
		date,
		hospitalData,
		weekIndex,
		dayIndex,
		showActual,
		prepareDataForAddNurse,
		track,
		sectionName,
		sectionLocationKey,
		onClose,
		addNurse,
		chosenDepartment.department_id,
		isDraftMode,
		staffRequests,
		suggestCloseOpenShift,
		jobManagementEnabled,
		isHospital,
		selectedJob,
	]);

	const getTargetCheck = useStaffingTargetCheck();
	const [
		warningState,
		checkWarnings,
		handleAgencyConfirm,
		handleOvertimeConfirm,
		handleDailyHoursConfirm,
		handleCancelWarnings,
		handlePtoConfirm,
		handleTargetConfirm,
		handleEmployeeStatusConfirm,
	] = useNurseWarnings(performSave, chosenDepartment.longShiftDuration);

	const handleSave = useCallback(() => {
		if (!selectedNurse) return;

		const weeklyShifts = hospitalData.weeks[weekIndex]?.weeklyShifts ?? [];
		const existingDailyHours = countDailyHours({
			nurseId: selectedNurse.id,
			departmentId: chosenDepartment.department_id,
			weeklyShifts,
			dayIndex,
			breakRulesConfig: chosenDepartment.breakRulesConfig ?? [],
			nurseUnion: selectedNurse.union,
		});

		const nurseRole = NurseRole[NurseFullRoleShort[selectedNurse.role] as keyof typeof NurseRole];
		const shiftWorkHours = getShiftWorkHours(
			chosenDepartment.department_id,
			endTime,
			startTime,
			chosenDepartment.breakRulesConfig ?? [],
			nurseRole,
			selectedNurse.employmentType,
			selectedNurse.jobIds,
			jobManagementEnabled,
			selectedNurse.union,
		);

		const dailyHours = existingDailyHours + shiftWorkHours;
		const isAgency = isAgencyNurse(selectedNurse.employmentType);
		const hasPto = selectedNurse.employeeSpecificPreferences.some((pref) => isInPTO(pref, startTime, chosenDepartment.dayStart, endTime));
		const shiftNo = getShiftIndexByShiftTimes({ startTime, endTime });
		const targetWarning = getTargetCheck({ locationName: selectedLocation, weekIndex, dayNo: dayIndex, shiftIndex: shiftNo, isActual: showActual });

		checkWarnings(
			isAgency,
			undefined,
			undefined,
			dailyHours,
			showActual,
			hasPto,
			targetWarning,
			buildEmployeeStatusWarning(selectedNurse, chosenDepartment.department_id, startTime),
		);
	}, [
		selectedNurse,
		selectedLocation,
		hospitalData.weeks,
		weekIndex,
		dayIndex,
		chosenDepartment,
		startTime,
		endTime,
		showActual,
		checkWarnings,
		getTargetCheck,
	]);

	return (
		<>
			<Dialog
				open
				onClose={onClose}
				maxWidth={false}
				data-testid="quick-add-nurse-dialog"
				slotProps={{
					paper: {
						sx: {
							borderRadius: 2,
							width: { xs: "50%", xl: "30%" },
							height: "auto",
						},
					},
				}}
			>
				<DialogTitle sx={{ pb: 1 }}>
					<Typography sx={(theme) => ({ fontWeight: 600, fontSize: "16px", color: theme.palette.primary.main })}>{t("add.staff")}</Typography>
				</DialogTitle>
				<DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
					<StaffAutoComplete
						nurses={nurseOptions}
						value={selectedNurse ? { label: formatPersonName(selectedNurse), value: selectedNurse } : null}
						onChange={(_, value) => handleNurseChange(value?.value ?? null)}
						fullWidth
						autoFocus
					/>
					{/* Only meaningful in job mode — in role mode the shift's job is not what drives the
					    schedule, and the time picker resolves by role. */}
					{jobManagementEnabled && nurseJobs.length > 0 && (
						<JobSelector
							variant="dropdown"
							jobs={nurseJobs}
							selectedJobIds={selectedJobId ? [selectedJobId] : []}
							onSelectedJobsChanged={(selected: string[]) => setSelectedJobId(selected[0])}
							hideAllOption
							singleSelect
							label={t("job")}
						/>
					)}
					{locationOptions.length > 0 && (
						<SystemAutoComplete
							options={locationOptions}
							value={selectedLocation ? { label: selectedLocation, value: selectedLocation } : null}
							onChange={(_, value) => setSelectedLocation(value?.value ?? null)}
							textFieldProps={{ label: t("location"), placeholder: t("select.location") }}
							dataTestId="quick-add-nurse-location-input"
							fullWidth
						/>
					)}
					<EditTimePopover
						startTime={startTime}
						endTime={endTime}
						onTimeChange={({ newStartTime, newEndTime }) => {
							setStartTime(newStartTime);
							setEndTime(newEndTime);
						}}
						onClose={() => {}}
						anchorEl={null}
						menuWrapper={false}
						hideActions
						disableAutoCompetePortal={false}
						role={selectedNurse ? NurseFullRoleShort[selectedNurse.role] : "default"}
						jobId={selectedJobId}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<SystemButton buttonVariant="secondary" onClick={onClose} data-testid="quick-add-nurse-cancel-button">
						{t("cancel")}
					</SystemButton>
					<SystemButton loading={addNurseLoading} onClick={handleSave} disabled={!selectedNurse} data-testid="quick-add-nurse-submit-button">
						{t("save")}
					</SystemButton>
				</DialogActions>
			</Dialog>
			<WarningDialog
				openPtoWarning={warningState.isPtoWarningOpen}
				onCancelPto={handleCancelWarnings}
				onConfirmPto={handlePtoConfirm}
				openTargetWarning={warningState.isTargetWarningOpen}
				openAgencyWarning={warningState.isAgencyWarningOpen}
				openOvertimeWarning={warningState.isOvertimeWarningOpen}
				openDailyHoursWarning={warningState.isDailyHoursWarningOpen}
				openEmployeeStatusWarning={warningState.isEmployeeStatusWarningOpen}
				overtimeDetails={warningState.overtimeDetails}
				dailyHoursDetails={warningState.dailyHoursDetails}
				targetDetails={warningState.targetDetails}
				employeeStatusDetails={warningState.employeeStatusDetails}
				longShiftDuration={chosenDepartment.longShiftDuration}
				onCancelTarget={handleCancelWarnings}
				onConfirmTarget={handleTargetConfirm}
				onCancelAgency={handleCancelWarnings}
				onConfirmAgency={handleAgencyConfirm}
				onCancelOvertime={handleCancelWarnings}
				onConfirmOvertime={handleOvertimeConfirm}
				onCancelDailyHours={handleCancelWarnings}
				onConfirmDailyHours={handleDailyHoursConfirm}
				onCancelEmployeeStatus={handleCancelWarnings}
				onConfirmEmployeeStatus={handleEmployeeStatusConfirm}
			/>
		</>
	);
};
